import SelectableRow from '../../selectable_record_table/selectable_row';
import React from 'react';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { faArrowDownArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SelectableRecordTable from '../../selectable_record_table/selectable_record_table';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';

import './styles.scss';

// What an empty cell shows, here and in a report column that draws its own cells.
export const EMPTY_CELL = '--';

export default function ReportTable({
  allSelected,
  columns,
  isSelected,
  records,
  sortColumn,
  sortOrder,
  onRecordSelected,
  onSelectAll,
  onSortUpdate,
  reportType
}) {
  const clampTextStyle = {
    display: '-webkit-box',
    overflow: 'hidden',
    whiteSpace: 'normal',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2
  };

  const renderTableHeaders = () => {
    return columns.map((column) => {
      let sortIcon = faArrowDownArrowUp;
      if (column.sortable === true && column.key === sortColumn) {
        sortIcon = sortOrder === 'asc' ? faArrowUp : faArrowDown;
      }

      const headerContent = (
        <>
          {column.name}
          {column.sortable && <FontAwesomeIcon className="ms-2" icon={sortIcon}/>}
        </>
      );

      return (
        <th
          className={classNames('text-nowrap', { clickable: column.sortable, 'text-end': column.align === 'end' })}
          key={column.key}
          onClick={column.sortable === true ? () => onSortUpdate(column.key) : () => {}}
        >
          {column.tooltip ? (
            <OverlayTrigger
              overlay={<Tooltip id={`tooltip-${column.key}`}>{column.tooltip}</Tooltip>}
              placement="top"
            >
              <span>{headerContent}</span>
            </OverlayTrigger>
          ) : (
            headerContent
          )}
        </th>
      );
    });
  };

  return (
    <SelectableRecordTable
      className="mb-0"
      columnHeaders={renderTableHeaders()}
      // Ticked over nothing, select-all would select every row the next fetch brings back.
      hasSelectAll={records.length > 0}
      hasSelectColumn={true}
      responsive={false}
      selectAllChecked={allSelected}
      stickyHeader
      tableProps={{ striped: true }}
      onSelectAllToggle={onSelectAll}
    >
      {records.map(record => (
        <SelectableRow
          disabled={allSelected}
          key={record.uniqueId || record.id}
          responsive={false}
          selected={isSelected(record)}
          onChange={() => onRecordSelected(record)}
        >
          {columns.map((column) => {
            const isCountCell = /Count$/.test(column.key) && typeof(record[column.key]) === 'number';
            let valueToShow = isCountCell ? record[column.key] : (record[column.key] || EMPTY_CELL);

            if (typeof valueToShow !== 'string') {
              valueToShow = JSON.stringify(valueToShow, '', 2);
            }

            const isAddressCell = column.key === 'address' && reportType === 'property';
            // linkKey names the row field holding the cell's link; a row without one shows plain text.
            const linkHref = isAddressCell ? `/projects/${record.id}` : column.linkKey && record[column.linkKey];
            const showHiddenRateBadge =
              column.key === 'availableLeaseRateRange' && record.availableLeaseRateHidden;
            const CellComponent = column.component;
            const content = CellComponent ? <CellComponent column={column} record={record} /> : valueToShow;

            return (
              <td className={classNames({ 'tabular-nums text-end': column.align === 'end' })} key={column.key}>
                {linkHref ? (
                  <a href={linkHref}>{content}</a>
                ) : showHiddenRateBadge ? (
                  <div className="d-flex align-items-center gap-1">
                    {valueToShow}
                    <OverlayTrigger
                      overlay={
                        <Tooltip id={`hidden-rate-${record.uniqueId || record.id}`}>
                          Rate hidden on public listings
                        </Tooltip>
                      }
                      placement="top"
                    >
                      <Badge appearance="muted" variant="secondary">Hidden</Badge>
                    </OverlayTrigger>
                  </div>
                ) : (
                  <div style={clampTextStyle}>
                    {content}
                  </div>
                )}
              </td>
            );
          })}
        </SelectableRow>
      ))}
    </SelectableRecordTable>
  );
}

ReportTable.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  columns: PropTypes.array.isRequired,
  isSelected: PropTypes.func.isRequired,
  records: PropTypes.array.isRequired,
  reportType: PropTypes.oneOf(['property', 'leaseComp', 'saleComp', 'user', 'listing']),
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  onRecordSelected: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onSortUpdate: PropTypes.func.isRequired,
};
